/**
 * Checklist Item Detail Modal/Panel
 * Permite editar un item del checklist con todas sus funcionalidades
 */

import { useState, useEffect } from 'react';
import { updateChecklistItem } from '../services/checklists';
import { listEvidences, uploadEvidence, generateUploadUrl, downloadEvidence } from '../services/evidences';
import { createMeasurement, listMeasurements } from '../services/measurements';
import { createFinding, listFindings } from '../services/findings';
import { askQuestion } from '../services/rag';
import { useAuth } from '../contexts/AuthContext';
import type { Evidence, Measurement, ComplianceFinding } from '../../../shared/src/types';
import '../styles/ChecklistItemDetail.css';

interface ChecklistItemDetailProps {
  item: any; // ChecklistItemRun with template info
  checklistRunId: string;
  installationId: string;
  onClose: () => void;
  onUpdate: () => void;
}

export default function ChecklistItemDetail({
  item,
  checklistRunId,
  installationId,
  onClose,
  onUpdate,
}: ChecklistItemDetailProps) {
  const { user } = useAuth();
  const [notes, setNotes] = useState(item.notes || '');
  const [status, setStatus] = useState(item.status);
  const [evidences, setEvidences] = useState<Evidence[]>([]);
  const [measurements, setMeasurements] = useState<Measurement[]>([]);
  const [findings, setFindings] = useState<ComplianceFinding[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'notes' | 'evidences' | 'measurements' | 'findings' | 'assistant'>('notes');

  // Evidence upload
  const [uploadingEvidence, setUploadingEvidence] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Measurement form
  const [measurementType, setMeasurementType] = useState('');
  const [measurementValue, setMeasurementValue] = useState('');
  const [measurementUnit, setMeasurementUnit] = useState('');
  const [measurementNotes, setMeasurementNotes] = useState('');

  // Finding form
  const [findingSeverity, setFindingSeverity] = useState<'critical' | 'high' | 'medium' | 'low'>('medium');
  const [findingDescription, setFindingDescription] = useState('');
  const [findingRecommendation, setFindingRecommendation] = useState('');

  // Assistant
  const [assistantQuestion, setAssistantQuestion] = useState('');
  const [assistantLoading, setAssistantLoading] = useState(false);
  const [assistantAnswer, setAssistantAnswer] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [item.id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [evs, meas, finds] = await Promise.all([
        listEvidences(checklistRunId, item.id),
        listMeasurements(installationId, item.id),
        listFindings(installationId, checklistRunId),
      ]);
      setEvidences(evs);
      setMeasurements(meas);
      // Filter findings for this specific item
      setFindings(finds.filter((f: ComplianceFinding) => f.checklist_item_run_id === item.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      await updateChecklistItem(checklistRunId, item.id, {
        status: status as any,
        notes: notes || undefined,
      });

      onUpdate();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error saving item');
    } finally {
      setSaving(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUploadEvidence = async () => {
    if (!selectedFile) return;

    try {
      setUploadingEvidence(true);
      setError('');

      // Generate upload URL
      const { evidenceId } = await generateUploadUrl({
        installation_id: installationId,
        checklist_run_id: checklistRunId,
        checklist_item_run_id: item.id,
        filename: selectedFile.name,
        content_type: selectedFile.type,
      });

      // Upload file using the presigned URL
      // Note: The uploadUrl from backend is a relative path with token
      // We need to upload to the API endpoint
      await uploadEvidence(evidenceId, selectedFile);
      
      // Reload evidences
      await loadData();
      setSelectedFile(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error uploading evidence');
    } finally {
      setUploadingEvidence(false);
    }
  };

  const handleCreateMeasurement = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setSaving(true);
      setError('');

      await createMeasurement({
        installation_id: installationId,
        checklist_item_run_id: item.id,
        type: measurementType,
        value: parseFloat(measurementValue),
        unit: measurementUnit,
        notes: measurementNotes || undefined,
      });

      // Reset form
      setMeasurementType('');
      setMeasurementValue('');
      setMeasurementUnit('');
      setMeasurementNotes('');

      // Reload measurements
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error creating measurement');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateFinding = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setSaving(true);
      setError('');

      await createFinding({
        installation_id: installationId,
        checklist_run_id: checklistRunId,
        checklist_item_run_id: item.id,
        severity: findingSeverity,
        description: findingDescription,
        recommendation: findingRecommendation || undefined,
      });

      // Reset form
      setFindingSeverity('medium');
      setFindingDescription('');
      setFindingRecommendation('');

      // Reload findings
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error creating finding');
    } finally {
      setSaving(false);
    }
  };

  const handleAskAssistant = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setAssistantLoading(true);
      setAssistantAnswer(null);
      setError('');

      const response = await askQuestion({
        question: assistantQuestion,
        installation_id: installationId,
        checklist_item_id: item.id,
      });

      setAssistantAnswer(response.answer);
      setAssistantQuestion('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error consulting assistant');
    } finally {
      setAssistantLoading(false);
    }
  };

  const canEdit = user?.role === 'Admin' || user?.role === 'Supervisor' || user?.role === 'Técnico';

  return (
    <div className="checklist-item-detail-overlay" onClick={onClose}>
      <div className="checklist-item-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>{item.template_item_name || 'Item del Checklist'}</h2>
            {item.template_item_itc_reference && (
              <span className="itc-badge">{item.template_item_itc_reference}</span>
            )}
          </div>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>

        {item.template_item_description && (
          <div className="item-description-section">
            <p>{item.template_item_description}</p>
            {item.template_item_requires_evidence && (
              <span className="evidence-required-badge">Requiere evidencia</span>
            )}
            {item.template_item_required && (
              <span className="required-badge">Obligatorio</span>
            )}
          </div>
        )}

        {error && <div className="error-message">{error}</div>}

        <div className="modal-tabs">
          <button
            className={activeTab === 'notes' ? 'active' : ''}
            onClick={() => setActiveTab('notes')}
          >
            Notas
          </button>
          <button
            className={activeTab === 'evidences' ? 'active' : ''}
            onClick={() => setActiveTab('evidences')}
          >
            Evidencias ({evidences.length})
          </button>
          <button
            className={activeTab === 'measurements' ? 'active' : ''}
            onClick={() => setActiveTab('measurements')}
          >
            Mediciones ({measurements.length})
          </button>
          <button
            className={activeTab === 'findings' ? 'active' : ''}
            onClick={() => setActiveTab('findings')}
          >
            Hallazgos ({findings.length})
          </button>
          <button
            className={activeTab === 'assistant' ? 'active' : ''}
            onClick={() => setActiveTab('assistant')}
          >
            Asistente
          </button>
        </div>

        <div className="modal-content">
          {loading && <div className="loading">Cargando datos...</div>}
          {!loading && activeTab === 'notes' && (
            <div className="tab-content">
              <div className="form-field">
                <label htmlFor="status">Estado *</label>
                <select
                  id="status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  disabled={!canEdit}
                >
                  <option value="pending">Pendiente</option>
                  <option value="in_progress">En progreso</option>
                  <option value="completed">Completado</option>
                  <option value="blocked">Bloqueado</option>
                  <option value="failed">Fallido</option>
                </select>
                <small className="form-help">
                  {status === 'completed' && item.template_item_requires_evidence && evidences.length === 0 && (
                    <span className="warning-text">⚠ Este item requiere evidencia antes de completarse</span>
                  )}
                  {status === 'completed' && item.template_item_required && (
                    <span className="info-text">✓ Item obligatorio completado</span>
                  )}
                </small>
              </div>

              <div className="form-field">
                <label htmlFor="notes">Notas</label>
                <textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={6}
                  placeholder="Agregar observaciones, comentarios, condiciones encontradas..."
                  disabled={!canEdit}
                />
                <small className="form-help">Las notas son visibles para todos los usuarios con acceso a este checklist</small>
              </div>

              {item.template_item_description && (
                <div className="item-info-box">
                  <strong>Descripción del item:</strong>
                  <p>{item.template_item_description}</p>
                </div>
              )}
            </div>
          )}

          {!loading && activeTab === 'evidences' && (
            <div className="tab-content">
              <div className="upload-section">
                <input
                  type="file"
                  id="evidence-file"
                  accept="image/*,application/pdf"
                  onChange={handleFileSelect}
                  disabled={!canEdit}
                />
                {selectedFile && (
                  <div className="selected-file">
                    <span>{selectedFile.name}</span>
                    <button
                      className="btn-primary"
                      onClick={handleUploadEvidence}
                      disabled={uploadingEvidence}
                    >
                      {uploadingEvidence ? 'Subiendo...' : 'Subir'}
                    </button>
                  </div>
                )}
              </div>

              <div className="evidences-list">
                {evidences.length === 0 ? (
                  <p className="empty-state">No hay evidencias subidas</p>
                ) : (
                  evidences.map((evidence) => (
                    <div key={evidence.id} className="evidence-item">
                      <div className="evidence-info">
                        <span className="evidence-filename">{evidence.filename}</span>
                        <span className="evidence-type">{evidence.type}</span>
                        <span className="evidence-date">
                          {new Date(evidence.created_at).toLocaleDateString('es-ES')}
                        </span>
                      </div>
                      <div className="evidence-actions">
                        <button
                          className="btn-secondary btn-sm"
                          onClick={async () => {
                            try {
                              const blob = await downloadEvidence(evidence.id);
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = evidence.filename;
                              document.body.appendChild(a);
                              a.click();
                              window.URL.revokeObjectURL(url);
                              document.body.removeChild(a);
                            } catch (err) {
                              setError(err instanceof Error ? err.message : 'Error descargando evidencia');
                            }
                          }}
                        >
                          Descargar
                        </button>
                        {evidence.type === 'photo' && (
                          <button
                            className="btn-secondary btn-sm"
                            onClick={async () => {
                              try {
                                const blob = await downloadEvidence(evidence.id);
                                const url = window.URL.createObjectURL(blob);
                                window.open(url, '_blank');
                                // Clean up after a delay
                                setTimeout(() => window.URL.revokeObjectURL(url), 1000);
                              } catch (err) {
                                setError(err instanceof Error ? err.message : 'Error visualizando evidencia');
                              }
                            }}
                          >
                            Ver
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {!loading && activeTab === 'measurements' && (
            <div className="tab-content">
              <form onSubmit={handleCreateMeasurement}>
                <div className="form-field">
                  <label htmlFor="measurement-type">Tipo *</label>
                  <input
                    id="measurement-type"
                    type="text"
                    value={measurementType}
                    onChange={(e) => setMeasurementType(e.target.value)}
                    placeholder="Ej: Resistencia de aislamiento"
                    required
                    disabled={!canEdit}
                  />
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <label htmlFor="measurement-value">Valor *</label>
                    <input
                      id="measurement-value"
                      type="number"
                      step="any"
                      value={measurementValue}
                      onChange={(e) => setMeasurementValue(e.target.value)}
                      required
                      disabled={!canEdit}
                    />
                  </div>

                  <div className="form-field">
                    <label htmlFor="measurement-unit">Unidad *</label>
                    <input
                      id="measurement-unit"
                      type="text"
                      value={measurementUnit}
                      onChange={(e) => setMeasurementUnit(e.target.value)}
                      placeholder="Ej: MΩ, V, A"
                      required
                      disabled={!canEdit}
                    />
                  </div>
                </div>

                <div className="form-field">
                  <label htmlFor="measurement-notes">Notas</label>
                  <textarea
                    id="measurement-notes"
                    value={measurementNotes}
                    onChange={(e) => setMeasurementNotes(e.target.value)}
                    rows={3}
                    disabled={!canEdit}
                  />
                </div>

                {canEdit && (
                  <button type="submit" className="btn-primary" disabled={saving}>
                    {saving ? 'Guardando...' : 'Agregar medición'}
                  </button>
                )}
              </form>

              <div className="measurements-list">
                {measurements.length === 0 ? (
                  <p className="empty-state">No hay mediciones registradas</p>
                ) : (
                  measurements.map((measurement) => (
                    <div key={measurement.id} className="measurement-item">
                      <div className="measurement-header">
                        <strong>{measurement.type}</strong>
                        <span>{measurement.value} {measurement.unit}</span>
                      </div>
                      {measurement.notes && (
                        <p className="measurement-notes">{measurement.notes}</p>
                      )}
                      <span className="measurement-date">
                        {new Date(measurement.created_at).toLocaleDateString('es-ES')}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {!loading && activeTab === 'findings' && (
            <div className="tab-content">
              <form onSubmit={handleCreateFinding}>
                <div className="form-field">
                  <label htmlFor="finding-severity">Severidad *</label>
                  <select
                    id="finding-severity"
                    value={findingSeverity}
                    onChange={(e) => setFindingSeverity(e.target.value as any)}
                    required
                    disabled={!canEdit}
                  >
                    <option value="low">Baja</option>
                    <option value="medium">Media</option>
                    <option value="high">Alta</option>
                    <option value="critical">Crítica</option>
                  </select>
                </div>

                <div className="form-field">
                  <label htmlFor="finding-description">Descripción *</label>
                  <textarea
                    id="finding-description"
                    value={findingDescription}
                    onChange={(e) => setFindingDescription(e.target.value)}
                    rows={4}
                    placeholder="Describe el incumplimiento o problema encontrado..."
                    required
                    disabled={!canEdit}
                  />
                </div>

                <div className="form-field">
                  <label htmlFor="finding-recommendation">Recomendación</label>
                  <textarea
                    id="finding-recommendation"
                    value={findingRecommendation}
                    onChange={(e) => setFindingRecommendation(e.target.value)}
                    rows={3}
                    placeholder="Recomendaciones para resolver el problema..."
                    disabled={!canEdit}
                  />
                </div>

                {canEdit && (
                  <button type="submit" className="btn-primary" disabled={saving}>
                    {saving ? 'Guardando...' : 'Crear hallazgo'}
                  </button>
                )}
              </form>

              <div className="findings-list">
                {findings.length === 0 ? (
                  <p className="empty-state">No hay hallazgos registrados</p>
                ) : (
                  findings.map((finding) => (
                    <div key={finding.id} className={`finding-item finding-${finding.severity}`}>
                      <div className="finding-header">
                        <strong>{finding.severity}</strong>
                        <span className="finding-status">{finding.status}</span>
                      </div>
                      <p>{finding.description}</p>
                      {finding.recommendation && (
                        <p className="finding-recommendation">{finding.recommendation}</p>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {!loading && activeTab === 'assistant' && (
            <div className="tab-content">
              <form onSubmit={handleAskAssistant}>
                <div className="form-field">
                  <label htmlFor="assistant-question">Consulta al asistente normativo</label>
                  <textarea
                    id="assistant-question"
                    value={assistantQuestion}
                    onChange={(e) => setAssistantQuestion(e.target.value)}
                    rows={3}
                    placeholder={`Pregunta sobre ${item.template_item_name}...`}
                  />
                </div>
                <button type="submit" className="btn-primary" disabled={assistantLoading || !assistantQuestion.trim()}>
                  {assistantLoading ? 'Consultando...' : 'Consultar'}
                </button>
              </form>

              {assistantAnswer && (
                <div className="assistant-answer">
                  <h4>Respuesta:</h4>
                  <p>{assistantAnswer}</p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Cerrar
          </button>
          {canEdit && (
            <button className="btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar cambios'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

